import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, X, ArrowUpDown, MapPin, Star, TrendingUp, PoundSterling, Navigation } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VenueCard from "../components/VenueCard";
import FilterPanel from "../components/FilterPanel";
import AdBanner from "../components/AdBanner";
import FeaturedVenue from "../components/FeaturedVenue";
import SEO from "../components/SEO";
import EzoicAd from "../components/EzoicAd";
import { trackPageView, incrementPageCount } from "../utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sortOptions = [
  { value: "", label: "Default", icon: ArrowUpDown },
  { value: "nearest", label: "Nearest First", icon: Navigation },
  { value: "popularity", label: "Most Popular", icon: TrendingUp },
  { value: "rating", label: "Top Rated", icon: Star },
  { value: "price", label: "Lowest Price", icon: PoundSterling },
];

const radiusOptions = [
  { value: 5, label: "5 km" },
  { value: 10, label: "10 km" },
  { value: 25, label: "25 km" },
  { value: 50, label: "50 km" },
  { value: 100, label: "100 km" },
  { value: "", label: "Any distance" },
];

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [sortBy, setSortBy] = useState("");
  const [radius, setRadius] = useState(50);
  const [userLocation, setUserLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  const [filters, setFilters] = useState({
    venue_type: searchParams.get("type") || "",
    wifi_quality: "",
    noise_level: "",
    has_meeting_rooms: null,
    has_parking: null,
    min_power_outlets: null,
    is_premium: null,
    city: "",
    min_rating: null,
    // New granular filters
    long_stay_friendly: null,
    good_for_calls: null,
    has_outdoor_seating: null,
    has_ac: null,
    is_spacious: null,
    natural_light: null,
    pet_friendly: null,
    wheelchair_accessible: null,
    group_friendly: null,
    has_standing_desks: null,
    has_food: null
  });

  useEffect(() => {
    // Track visitor with enhanced analytics - only on mount
    trackPageView("browse", {
      search_query: searchQuery || null,
      filters_used: null
    });
    incrementPageCount();
    
    // Try to get user location on mount
    getUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchQuery, sortBy, radius, userLocation]);

  const getUserLocation = () => {
    if ("geolocation" in navigator) {
      setGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setGettingLocation(false);
        },
        (error) => {
          // Location not available - not an error, user may have denied permission
          setGettingLocation(false);
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
      );
    }
  };

  const fetchVenues = async () => {
    setLoading(true);
    try {
      const params = { search: searchQuery || undefined };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null) params[key] = value;
      });
      
      // Add sort and location params
      if (sortBy) params.sort_by = sortBy;
      if (userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
        if (radius) params.radius_km = radius;
      }
      
      const response = await axios.get(`${API}/venues`, { params });
      setVenues(response.data);
    } catch (error) {
      console.error("Error fetching venues:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(prev => {
      if (searchQuery) prev.set("search", searchQuery);
      else prev.delete("search");
      return prev;
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      venue_type: "", wifi_quality: "", noise_level: "",
      has_meeting_rooms: null, has_parking: null, min_power_outlets: null,
      is_premium: null, city: "", min_rating: null,
      // New filters
      long_stay_friendly: null, good_for_calls: null, has_outdoor_seating: null,
      has_ac: null, is_spacious: null, natural_light: null, pet_friendly: null,
      wheelchair_accessible: null, group_friendly: null, has_standing_desks: null,
      has_food: null
    });
    setSearchQuery("");
    setSortBy("");
    setRadius(50);
    setSearchParams({});
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== "" && v !== null).length + (searchQuery ? 1 : 0);

  return (
    <div className="min-h-screen bg-surface">
      <SEO 
        title="Browse Remote Workspaces Near You"
        description="Search and filter cafes, pubs, hotels, libraries and coworking hubs by WiFi speed, power outlets, noise level and more. Find your ideal remote work spot in UK."
        keywords="browse workspaces, find cafe with WiFi, coworking spaces UK, remote work venues, filter workspaces, laptop friendly venues"
        canonical="/browse"
      />
      <Header />
      
      <div className="pt-16">
        {/* Search Bar */}
        <div className="sticky top-16 z-30 bg-white border-b border-gray-100 px-4 py-3">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search venues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-lg bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
                  data-testid="browse-search-input"
                />
              </div>
            </form>
            
            <div className="flex items-center gap-2">
              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="h-10 pl-3 pr-8 rounded-lg bg-gray-50 border border-gray-200 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-brand/20 focus:border-brand appearance-none cursor-pointer"
                  data-testid="sort-select"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ArrowUpDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              
              {/* Radius Dropdown (only show if location available) */}
              {userLocation && (
                <div className="relative">
                  <select
                    value={radius}
                    onChange={(e) => setRadius(e.target.value ? Number(e.target.value) : "")}
                    className="h-10 pl-3 pr-8 rounded-lg bg-gray-50 border border-gray-200 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-brand/20 focus:border-brand appearance-none cursor-pointer"
                    data-testid="radius-select"
                  >
                    {radiusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <MapPin className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              )}
              
              {/* Get Location Button (if no location) */}
              {!userLocation && (
                <button
                  onClick={getUserLocation}
                  disabled={gettingLocation}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  data-testid="get-location-btn"
                >
                  <MapPin className="w-4 h-4" />
                  {gettingLocation ? "Finding..." : "Use Location"}
                </button>
              )}
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  showFilters || activeFilterCount > 0
                    ? "bg-brand text-white"
                    : "bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100"
                }`}
                data-testid="filter-toggle-btn"
              >
                <Filter className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-white/20 text-xs flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <FilterPanel filters={filters} onChange={handleFilterChange} onClear={clearFilters} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Main Content - List Only */}
        <div className="max-w-6xl mx-auto p-4">
          {/* Featured Venue of the Week */}
          <div className="mb-6">
            <FeaturedVenue />
          </div>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">
                {loading ? "Loading..." : `${venues.length} workspaces`}
              </p>
              {userLocation && sortBy === "nearest" && (
                <span className="text-xs text-brand font-medium flex items-center gap-1">
                  <Navigation className="w-3 h-3" /> Sorted by distance
                </span>
              )}
            </div>
            {(activeFilterCount > 0 || sortBy || (radius && radius !== 50)) && (
              <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-brand hover:text-brand-dark" data-testid="clear-filters-btn">
                <X className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : venues.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No venues found</p>
              {radius && userLocation && (
                <p className="text-sm text-gray-400 mt-1">Try increasing the search radius</p>
              )}
              <button onClick={clearFilters} className="mt-3 btn-secondary text-sm">Clear Filters</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {venues.slice(0, 6).map((venue, index) => (
                  <motion.div
                    key={venue.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <VenueCard venue={venue} />
                  </motion.div>
                ))}
              </div>
              
              {/* Ad Banner after first 6 results */}
              {venues.length > 6 && (
                <div className="my-6">
                  <AdBanner adSlot="" adFormat="horizontal" className="rounded-xl overflow-hidden" />
                </div>
              )}
              
              {/* Remaining venues */}
              {venues.length > 6 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {venues.slice(6).map((venue, index) => (
                    <motion.div
                      key={venue.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (index + 6) * 0.03 }}
                    >
                      <VenueCard venue={venue} />
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Ezoic Ad - Bottom of results */}
        <div className="mt-8">
          <EzoicAd placementId={104} />
        </div>
      </div>
      <Footer />
    </div>
  );
}
